import { createEffect, createSignal, type JSX } from "solid-js";

interface Props {
  value: number | null | undefined;
  onChange: (value: number | null) => void;
  class?: string;
  placeholder?: string;
  /** Minimum allowed value when committing (default 0). */
  min?: number;
  /** Maximum allowed value when committing. */
  max?: number;
  /** Round to this many decimal places on commit (default 2). */
  decimals?: number;
  disabled?: boolean;
  id?: string;
  /** Select the whole value on focus so a new number replaces it. */
  selectOnFocus?: boolean;
  "aria-label"?: string;
}

/**
 * Mobile-friendly decimal input.
 * Uses text + inputmode=decimal so partial values like "80," / "80." stay editable.
 * Accepts both comma and dot as decimal separator.
 */
export default function DecimalField(props: Props) {
  const [focused, setFocused] = createSignal(false);
  const [draft, setDraft] = createSignal("");

  function formatCommitted(v: number | null | undefined): string {
    if (v == null || !Number.isFinite(v)) return "";
    // Avoid trailing ".0" noise for integers; keep decimals when present
    const d = props.decimals ?? 2;
    const rounded = roundTo(v, d);
    return Number.isInteger(rounded) ? String(rounded) : String(rounded);
  }

  // Sync draft from external value when not editing
  createEffect(() => {
    if (focused()) return;
    setDraft(formatCommitted(props.value));
  });

  function roundTo(n: number, decimals: number): number {
    const f = 10 ** Math.max(0, decimals);
    return Math.round(n * f) / f;
  }

  /** True while the user may still be typing (trailing separator ok). */
  function isEditablePartial(raw: string): boolean {
    return /^[0-9]*([.,][0-9]*)?$/.test(raw.trim());
  }

  function parseLoose(raw: string): number | null {
    const t = raw.trim().replace(",", ".");
    if (t === "" || t === "." || t === "-") return null;
    // Incomplete: "12." → still partial, don't force-commit as 12 until blur
    if (t.endsWith(".")) return null;
    const n = Number(t);
    if (!Number.isFinite(n)) return null;
    return n;
  }

  function clamp(n: number): number {
    let x = n;
    if (props.min != null && x < props.min) x = props.min;
    if (props.max != null && x > props.max) x = props.max;
    return roundTo(x, props.decimals ?? 2);
  }

  function commitFromDraft(raw: string) {
    const cleaned = raw.trim().replace(",", ".");
    if (cleaned === "" || cleaned === ".") {
      props.onChange(null);
      setDraft("");
      return;
    }
    const n = Number(cleaned.endsWith(".") ? cleaned.slice(0, -1) : cleaned);
    if (!Number.isFinite(n)) {
      setDraft(formatCommitted(props.value));
      return;
    }
    const next = clamp(n);
    props.onChange(next);
    setDraft(formatCommitted(next));
  }

  const display = (): string => (focused() ? draft() : formatCommitted(props.value));

  const onInput: JSX.EventHandlerUnion<HTMLInputElement, InputEvent> = (e) => {
    const v = e.currentTarget.value;
    if (v !== "" && !isEditablePartial(v)) return;
    setDraft(v);
    // Commit intermediate complete numbers so parent state stays live
    // but keep draft as typed (incl. trailing separator) while focused
    const parsed = parseLoose(v);
    if (parsed != null) {
      props.onChange(clamp(parsed));
    } else if (v.trim() === "") {
      props.onChange(null);
    }
  };

  return (
    <input
      id={props.id}
      class={props.class}
      type="text"
      inputmode="decimal"
      enterkeyhint="done"
      autocomplete="off"
      autocorrect="off"
      spellcheck={false}
      disabled={props.disabled}
      placeholder={props.placeholder}
      aria-label={props["aria-label"]}
      value={display()}
      onFocus={(e) => {
        setDraft(formatCommitted(props.value));
        setFocused(true);
        if (props.selectOnFocus) {
          const el = e.currentTarget;
          queueMicrotask(() => el.select());
        }
      }}
      onInput={onInput}
      onBlur={() => {
        commitFromDraft(draft());
        setFocused(false);
      }}
    />
  );
}
